"use client";

/**
 * Typed client for the V2 BI API (`/api/v2/{dashboards,widgets,queries}`).
 *
 * BI is plain REST on the Hono V2 API (NOT offline-first / Dexie), so it goes
 * through the shared `v2Fetch` transport — bearer auth, `credentials:"include"`,
 * dev/prod base-URL resolution, and `{ status, body }` on error. Grouped by
 * resource like the existing `v2Api` object in `./client`.
 *
 * Surface intentionally mirrors the backend JWT surface:
 *  - dashboards: full CRUD (per-user; the user owns dashboard lifecycle)
 *  - widgets:    read + the one user action `refresh` (mutations are AI-only)
 *  - queries:    read only (queries are AI-authored — no FE write path)
 */

import { v2Fetch } from "./client";
import type {
  Dashboard,
  DashboardCreateInput,
  DashboardListItem,
  DashboardPayload,
  DashboardUpdateInput,
  DashboardWidgetChanges,
  LayoutPlacementInput,
  Query,
  QueryExecutionPayload,
  QueryWithResult,
  SavedLayoutPlacement,
  Widget,
  WidgetRenameInput,
} from "./bi-types";

/** List endpoints wrap their array in `{ items }`. */
interface ListEnvelope<T> {
  items: T[];
}

/**
 * Machine-readable failure codes from `POST /widgets/:id/refresh`, mirroring the
 * backend `QueryExecutionError` subtypes + `ExecutionBusyError`
 * (`apps/api/src/domains/bi/query/execution/{errors,concurrency}.ts`).
 */
export type WidgetRefreshErrorCode =
  | "unsafe_query" // 400 — query failed static safety/access check
  | "access_denied" // 403 — touches a table/column above the caller's tier
  | "query_timeout" // 408 — exceeded statement_timeout
  | "read_only_violation" // 400 — write/DDL blocked by the read-only tx
  | "not_found" // 404 — widget or backing query is gone
  | "ambiguous_columns" // 400 — duplicate result column names
  | "result_too_large" // 413 — payload over the byte cap
  | "execution_busy"; // 429 — executor at capacity, retry shortly

const REFRESH_CODES: ReadonlySet<string> = new Set<WidgetRefreshErrorCode>([
  "unsafe_query",
  "access_denied",
  "query_timeout",
  "read_only_violation",
  "not_found",
  "ambiguous_columns",
  "result_too_large",
  "execution_busy",
]);

/**
 * Typed error thrown by `biApi.widgets.refresh`. Carries the backend's
 * machine-readable `code` (or `"unknown"` for anything unrecognised / a network
 * failure) and the HTTP `status`, so callers can branch — e.g. show a
 * "retry shortly" hint on `execution_busy` (429) vs a permission message on
 * `access_denied` (403).
 */
export class WidgetRefreshError extends Error {
  constructor(
    public readonly code: WidgetRefreshErrorCode | "unknown",
    public readonly status: number,
    message: string,
    public readonly offending?: string,
  ) {
    super(message);
    this.name = "WidgetRefreshError";
  }
}

function toWidgetRefreshError(err: unknown): WidgetRefreshError {
  const e = err as {
    status?: number;
    body?: { error?: string; message?: string; offending?: string };
    message?: string;
  };
  const status = typeof e?.status === "number" ? e.status : 0;
  const rawCode = e?.body?.error;
  const code =
    rawCode && REFRESH_CODES.has(rawCode)
      ? (rawCode as WidgetRefreshErrorCode)
      : "unknown";
  const message = e?.body?.message ?? e?.message ?? "Widget refresh failed";
  return new WidgetRefreshError(code, status, message, e?.body?.offending);
}

export const biApi = {
  dashboards: {
    list(): Promise<DashboardListItem[]> {
      return v2Fetch<ListEnvelope<DashboardListItem>>(
        "/api/v2/dashboards",
      ).then((r) => r.items);
    },
    get(id: string): Promise<DashboardPayload> {
      return v2Fetch<DashboardPayload>(`/api/v2/dashboards/${id}`);
    },
    /**
     * Incremental widget changes since `since` (a prior response's
     * `serverTime`), for live polling (STK-263). Omit `since` on the first
     * call to bootstrap the cursor (empty changes + a `serverTime` to poll
     * from). See {@link DashboardWidgetChanges}.
     */
    changes(
      id: string,
      since?: string | null,
    ): Promise<DashboardWidgetChanges> {
      const qs = since ? `?since=${encodeURIComponent(since)}` : "";
      return v2Fetch<DashboardWidgetChanges>(
        `/api/v2/dashboards/${id}/changes${qs}`,
      );
    },
    create(input: DashboardCreateInput): Promise<Dashboard> {
      return v2Fetch<Dashboard>("/api/v2/dashboards", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    update(id: string, input: DashboardUpdateInput): Promise<Dashboard> {
      return v2Fetch<Dashboard>(`/api/v2/dashboards/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      });
    },
    /**
     * Rewrite the user's tab order. `dashboardIds` must list EXACTLY the user's
     * live dashboards in the desired order; the backend rejects a mismatched
     * set (422). Returns the dashboards with their new `position`s.
     */
    reorder(dashboardIds: string[]): Promise<Dashboard[]> {
      return v2Fetch<Dashboard[]>("/api/v2/dashboards/reorder", {
        method: "PATCH",
        body: JSON.stringify({ dashboardIds }),
      });
    },
    /** Make a dashboard the user's default landing tab. */
    setDefault(id: string): Promise<Dashboard> {
      return v2Fetch<Dashboard>(`/api/v2/dashboards/${id}/default`, {
        method: "POST",
      });
    },
    /**
     * Report widgets the user has actually seen on screen (STK-1658). Batched
     * by the grid; idempotent, and ids from another board are ignored.
     */
    markWidgetsSeen(
      id: string,
      placementIds: string[],
    ): Promise<{ marked: number; markedIds: string[] }> {
      return v2Fetch<{ marked: number; markedIds: string[] }>(
        `/api/v2/dashboards/${id}/widgets/seen`,
        { method: "POST", body: JSON.stringify({ placementIds }) },
      );
    },
    /**
     * Persist a whole-dashboard grid layout the user arranged in edit mode.
     * `placements` is a full-set rewrite of every placement's
     * position/width/height; the backend 422s if the set is stale (its ids no
     * longer match the dashboard's live placements). Returns the rewritten
     * placements in position order.
     */
    saveLayout(
      id: string,
      placements: LayoutPlacementInput[],
    ): Promise<SavedLayoutPlacement[]> {
      return v2Fetch<{ placements: SavedLayoutPlacement[] }>(
        `/api/v2/dashboards/${id}/layout`,
        { method: "PATCH", body: JSON.stringify({ placements }) },
      ).then((r) => r.placements);
    },
    remove(id: string): Promise<Dashboard> {
      return v2Fetch<Dashboard>(`/api/v2/dashboards/${id}`, {
        method: "DELETE",
      });
    },
  },

  widgets: {
    list(): Promise<Widget[]> {
      return v2Fetch<ListEnvelope<Widget>>("/api/v2/widgets").then(
        (r) => r.items,
      );
    },
    get(id: string): Promise<Widget> {
      return v2Fetch<Widget>(`/api/v2/widgets/${id}`);
    },
    /**
     * Rename a widget's title (`PATCH /widgets/:id`, STK-734) — the one widget
     * field a user may edit (every other write is AI-authored). The backend
     * authorizes the rename against a dashboard the caller owns and trims/bounds
     * the title; returns the updated widget.
     */
    rename(id: string, title: string): Promise<Widget> {
      const input: WidgetRenameInput = { title };
      return v2Fetch<Widget>(`/api/v2/widgets/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      });
    },
    /**
     * Delete a widget (`DELETE /widgets/:id`) — the other user-facing widget
     * mutation (every remaining write is AI-authored). The backend authorizes
     * the delete against a dashboard the caller owns, then unpins the widget
     * from every owned dashboard and soft-deletes it; a missing/cross-user id
     * 404s. Resolves on success.
     */
    deleteWidget(id: string): Promise<void> {
      return v2Fetch<{ status: "ok" }>(`/api/v2/widgets/${id}`, {
        method: "DELETE",
      }).then(() => undefined);
    },
    /**
     * Re-run the widget's saved query under the caller's tier and return the
     * fresh result inline. Throws {@link WidgetRefreshError} on the mapped
     * failure statuses (400/403/404/408/413/429).
     */
    async refresh(id: string): Promise<QueryExecutionPayload> {
      try {
        return await v2Fetch<QueryExecutionPayload>(
          `/api/v2/widgets/${id}/refresh`,
          { method: "POST" },
        );
      } catch (err) {
        throw toWidgetRefreshError(err);
      }
    },
  },

  queries: {
    list(): Promise<Query[]> {
      return v2Fetch<ListEnvelope<Query>>("/api/v2/queries").then(
        (r) => r.items,
      );
    },
    get(id: string): Promise<QueryWithResult> {
      return v2Fetch<QueryWithResult>(`/api/v2/queries/${id}`);
    },
  },
};
